import './polyfill'

import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import i18n from './i18n'
import axios from 'axios'
import Rollbar from 'vue-rollbar'
import VCalendar from 'v-calendar'
import VueQuillEditor from 'vue-quill-editor'

import { mapState } from 'vuex'

import Message from './components/Message.vue'
import Loader from './components/Loader.vue'
import InputGroup from './components/InputGroup'
import PageView from './components/PageView'
import InputHeader from './components/InputHeader'

Vue.config.productionTip = false

// Access using `this.$rollbar`
Vue.use(Rollbar, {
  accessToken: process.env.VUE_APP_ROLLBAR_KEY,
  captureUncaught: true,
  captureUnhandledRejections: true,
  enabled: process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging',
  payload: {
    environment: process.env.NODE_ENV
  }
})

Vue.use(VCalendar)

Vue.use(VueQuillEditor, {
  modules: {
    toolbar: [
      ['bold', 'italic'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link']
    ]
  }
})

// Access axios in components through this.$axios
Vue.prototype.$axios = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
})

Vue.prototype.$qnAxios = axios.create({
  baseURL: process.env.VUE_APP_QUESTIONNAIRE_SERVICE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// REF: https://kapeli.com/cheat_sheets/Axios.docset/Contents/Resources/Documents/index
// REF: https://stackoverflow.com/questions/32261000/how-to-avoid-ajax-caching-in-internet-explorer-11-when-additional-query-string-p
Vue.prototype.$axios.interceptors.request.use(
  config => {
    if (config.method.toUpperCase() === 'GET') {
      config.headers = {
        ...config.headers,
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Expires': '-1',
        'Pragma': 'no-cache'
      }
    }
    return config
  },
  error => {
    return Promise.reject(error)
  }
)

Vue.mixin({
  components: {
    PageView,
    Message,
    Loader
  },
  computed: {
    ...mapState(['userAuthToken']),

    requestConfig () {
      return {
        headers: {
          'X-Auth-Token': this.userAuthToken
        }
      }
    }
  },
  methods: {
    // Iterates through form and object and checks for missing keys using the `data.required property` and `data.checkFields` if value is an object literal
    checkMissingFields (form, object) {
      for (let key in form) {
        // Check for existence of property `component` to avoid throwing of error
        if (form[key].hasOwnProperty('component')) {
          // If component is not `InputGroup`, it could be a form field
          if (form[key].component !== InputGroup) {
            // If component is `InputHeader`, it is not a form field
            // Otherwise, check if form field is required
            if (form[key].component !== InputHeader && form[key].data.required) {
              // If field value is falsy, it is empty
              if (!object[key]) {
                // Return `true` for missing vlaue
                return true
              // If field value is an array, it will always fail the test above
              // If field value is an array, and if array is empty
              } else if (Array.isArray(object[key]) && object[key].length === 0) {
                // Return `true` for empty array
                return true
              // If field value is an object literal, it will always fail the test above
              // If field value not an array and is of type 'object', it is an object literal
              } else if (!Array.isArray(object[key]) && typeof object[key] === 'object') {
                // Check that field configuration has property `checkFields` to avoid throwing of error
                if (form[key].data.hasOwnProperty('checkFields') && Array.isArray(form[key].data.checkFields)) {
                  // Go to `data.checkFields` for a list of properties to check for missing values
                  // Check every property to see if it is empty
                  return (
                    form[key].data.checkFields
                      // map each required property in object literal to `true` if it is empty
                      .map(field => object[key].hasOwnProperty(field) && !object[key][field])
                      // return `true` if every required property is empty
                      .every(field => field)
                  )
                }
                // Field configuration does not have property `checkFields`
                // So there is no basis to validate the object literal
                // Hence it is considered not to be empty and `false` is returned
                return false
              }
            }
          // If component is `InputGroup`, run a recursion to check the form fields within
          } else {
            return this.checkMissingFields(form[key].data, object[key])
          }
        }
      }
      return false
    },
    titleCase (string) {
      if (typeof string !== 'string') { return string }
      return (
        string
          .toLowerCase()
          .split(' ')
          .map(word => word.replace(word[0], typeof word[0] === 'string' ? word[0].toUpperCase() : word[0]))
          .join(' ')
      )
    },
    camelCase (string) {
      if (typeof string !== 'string') { return string }
      return (
        string
          .split(' ')
          .map(
            (word, index) => (
              word.replace(
                word[0],
                typeof word[0] === 'string'
                  ? (index === 0 ? word[0].toLowerCase() : word[0].toUpperCase())
                  : word[0])
            )
          )
          .join('')
      )
    },
    hasDigitsOnly (string) {
      return /^\d+$/.test(string)
    }
  }
})

new Vue({
  router,
  store,
  i18n,
  render: h => h(App)
}).$mount('#app')
